import React, { useState, Fragment } from 'react';
import { Dialog, Transition, Switch } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  PencilSquareIcon, 
  TrashIcon, 
  ShieldCheckIcon, 
  EnvelopeIcon,
  BriefcaseIcon,
  CalendarDaysIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';
import { TextInput } from '../../Shared/Component/forms/TextInput';
import { useFormController } from '../../Shared/Component/readFormData/useFormController';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import { DropdownSingle } from '../../Shared/Component/forms/DropdownSingle';
import { useRedirect } from '../../Shared/Hooks/useRedirect ';
import { initialScoreValueMappingRequest, scoreValueMappingRequest, scoreValueMappingResponse } from '../Types/AdminType';
import { createScoreValueMappingConfig, deleteScoreValueMappingConfig, updateScoreValueMappingConfig } from '../Services/AdminService';
import { useParams } from 'react-router-dom';

// 1. UPDATED INTERFACE
// Added necessary fields so TypeScript recognizes the properties used in your JSX
interface Recruiter {
  id: string;
  fieldName: string;
  score: string;
  fieldValue: string;
}

interface Props {
  data: scoreValueMappingResponse[];
  onEditSubmit: (data: Recruiter) => void;
  onDeleteConfirm: (email: string) => void;
  onRegisterSubmit: (data: Partial<Recruiter>) => void; 
  menuName: string;
}

const ScoreMappingConfigComponent = ({ data, onEditSubmit, onDeleteConfirm, onRegisterSubmit, menuName }: Props) => {
  const [selectedRecruiter, setSelectedRecruiter] = useState<Recruiter | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);

    const [adminData, setAdminData] = useState<scoreValueMappingRequest | null>(initialScoreValueMappingRequest);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>("");
    const [alert, setAlert] = useState<AlertProps | null>(null);

    const redirect = useRedirect();
    const { id } = useParams<{ id: string }>();

   const {
      values,
      setAllValues,
      handleInputChange,
      handleRequiredValueChange,
      handleValueChange,
      handleRequiredValueChangeForDD,
    } = useFormController<scoreValueMappingRequest>(initialScoreValueMappingRequest);

  const [newRecruiter, setNewRecruiter] = useState<Partial<Recruiter>>({
  fieldName: "",
  score: "",
  fieldValue: "",
  });

  // Casting as Recruiter here ensures compatibility with the map data
  const handleEditClick = (recruiter: any) => {
     setSelectedRecruiter(recruiter as Recruiter);
  // ✅ IMPORTANT: sync into form state
  setAllValues({
    fieldName: recruiter.fieldName || "",
    fieldValue: recruiter.fieldValue || "",
    score: recruiter.score || "",

  });

  setIsEditOpen(true);
  };

  const handleDeleteClick = (recruiter: any) => {
    setSelectedRecruiter(recruiter as Recruiter);
    setIsDeleteOpen(true);
    
  };


  

  
   const handleCreateSubmit = async () => {
    setAlert(null);

    const payload = {
    ...values,
    fieldName: id,   // 👈 override from URL
  };

    const response = await createScoreValueMappingConfig(payload);
  
    if (response.success) {
      setAdminData(values);
        setAlert({
          variant: "success",
          title: "Success...!",
          message: response.data.fieldName + " has been added successfully.", 
          autoCloseMs:5000,
           onClose: () => setAlert(null), 
        });
      } else {
        setAlert({
          variant: "error",
          title: "Registration Error",
          message: response.message, 
          autoCloseMs:5000,
           onClose: () => setAlert(null),
        });
      }
    }


  const handleEditSubmit = async () => {
    setAlert(null);
    
    const response = await updateScoreValueMappingConfig(values, selectedRecruiter?.id || "");
  
    if (response.success) {
      setAdminData(values);
        setAlert({
          variant: "success",
          title: "Success...!",
          message: response.data.fieldName + " has been updated successfully.", 
          autoCloseMs:5000,
           onClose: () => setAlert(null), 
        });
      } else {
        setAlert({
          variant: "error",
          title: "Registration Error",
          message: response.message, 
          autoCloseMs:5000,
           onClose: () => setAlert(null),
        });
      }
    }


    const handleDeleteSubmit = async () => {
    setAlert(null);
    const response = await deleteScoreValueMappingConfig(selectedRecruiter?.id || "");
    
    if (response.success) {
      setAdminData(values);
        setAlert({
          variant: "success",
          title: "Success...!",
          message:  selectedRecruiter?.fieldName + " has been deleted successfully.", 
          autoCloseMs:5000,
           onClose: () => setAlert(null), 
        });
      } else {
        setAlert({
          variant: "error",
          title: "Delete Error",
          message: response.message, 
          autoCloseMs:5000,
           onClose: () => setAlert(null),
        });
      }
    }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end px-4">
        <div>
          <h2 className="text-2xl font-black text-slate-800 tracking-tight">{menuName} Directory</h2>
          <p className="text-sm text-slate-500 font-medium">Manage and monitor team access levels</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05, translateY: -2 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsRegisterOpen(true)}
          className="flex items-center gap-2 px-6 py-3.5 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white rounded-2xl font-bold text-sm shadow-xl shadow-indigo-200 border border-indigo-400/20"
        >
          <UserPlusIcon className="w-5 h-5" />
          Add {menuName}
        </motion.button>
      </div>

       {alert && (
                  <div className="mb-6">
                    <Alert {...alert} onClose={() => setAlert(null)} />
                  </div>
                )}

      <div className="w-full p-1 bg-gradient-to-br from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-[2.5rem]">
      <div className="overflow-x-auto overflow-hidden bg-white/70 backdrop-blur-2xl rounded-[2.4rem] border border-white shadow-2xl">
      <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50">
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Field Name</th>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Field Value</th>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Score </th>

                <th className="px-6 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Role & Access</th>
                {/* <th className="px-6 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Created Date</th> */}
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100/50">
              {data.map((recruiter: any, idx) => (
                <motion.tr 
                  key={recruiter.targetRole}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="group hover:bg-white transition-all duration-300"
                >
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="font-bold text-slate-800 text-base">
                          {recruiter.fieldName || "?"}
                        </div>
                      </div>
                    </div>
                  </td>
                   <td className="px-8 py-5">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="font-bold text-slate-800 text-base">
                          {recruiter.fieldValue || "?"}
                        </div>
                      </div>
                    </div>
                  </td>
                   <td className="px-8 py-5">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="font-bold text-slate-800 text-base">
                          {recruiter.score || "?"}
                        </div>
                      </div>
                    </div>
                  </td>
                 


                   
                  <td className="px-6 py-5">
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                        <BriefcaseIcon className="w-4 h-4 text-indigo-400" />
                        Admin
                      </div>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-600 text-[10px] font-black uppercase border border-amber-100">
                          <ShieldCheckIcon className="w-3 h-3" /> System Admin
                        </span>
                    </div>
                  </td>
                
                  {/* <td className="px-6 py-5">
                    <div className="flex items-center gap-2 text-sm text-slate-500 font-semibold">
                      <CalendarDaysIcon className="w-4 h-4 text-slate-300" />
                      {recruiter.createdAt ? new Date(recruiter.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : "N/A"}
                    </div>
                  </td> */}
                  <td className="px-8 py-5 text-right">
                    <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => handleEditClick(recruiter)}
                        className="p-2.5 bg-white text-indigo-600 hover:bg-indigo-600 hover:text-white rounded-xl shadow-sm border border-indigo-50 transition-all active:scale-95"
                      >
                        <PencilSquareIcon className="w-5 h-5" />
                      </button>
                      <button 
                        onClick={() => handleDeleteClick(recruiter)}
                        className="p-2.5 bg-white text-rose-500 hover:bg-rose-500 hover:text-white rounded-xl shadow-sm border border-rose-50 transition-all active:scale-95"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- REGISTER MODAL --- */}
      <Transition show={isRegisterOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={() => setIsRegisterOpen(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95 translate-y-8" enterTo="opacity-100 scale-100 translate-y-0" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100 translate-y-0" leaveTo="opacity-0 scale-95 translate-y-8">
                <Dialog.Panel className="w-full max-w-xl bg-white rounded-[3rem] p-10 shadow-2xl border border-slate-100 relative">
                  <div className="flex justify-between items-center mb-8">
                    <div className="space-y-1">
                      <Dialog.Title className="text-3xl font-black text-slate-800 tracking-tight">Create New {menuName}</Dialog.Title>
                      <p className="text-sm text-slate-500 font-medium">Add a new {menuName} category to your Job Portal</p>
                    </div>
                    <button onClick={() => setIsRegisterOpen(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                      <XMarkIcon className="w-6 h-6 text-slate-400" />
                    </button>
                  </div>

                  <div className="space-y-5">
                    <TextInput
                      label="Field Name"
                      name="fieldName"
                      placeholder="Enter the fieldName"
                      value={id}
                      onChange={handleInputChange("fieldName")}
                        readOnly={true}
                    />

                     <TextInput
                      label="Field Value"
                      name="fieldType"
                      placeholder="Enter the fieldType"
                      value={values.fieldValue}
                      onChange={handleInputChange("fieldValue")}
                    
                    />

                    <TextInput
                      label="Score Type"
                      name="score"
                      placeholder="Enter the scoreType"
                      value={values.score.toString()}
                      onChange={handleInputChange("score")}
                    />

                    

                    <button 
                      onClick={() => { handleCreateSubmit(); onRegisterSubmit(newRecruiter); setIsRegisterOpen(false); }}
                      className="w-full py-4 bg-indigo-600 text-white font-black rounded-2xl shadow-xl shadow-indigo-200 hover:bg-indigo-700 transition-all transform hover:-translate-y-1 active:scale-[0.98] mt-4"
                    >
                      Create {menuName}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* --- EDIT MODAL --- */}
      <Transition show={isEditOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={() => setIsEditOpen(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-lg bg-white rounded-[3rem] p-10 shadow-2xl">
                  <div className="flex justify-between items-center mb-8">
                    <Dialog.Title className="text-3xl font-black text-slate-800">Edit {selectedRecruiter?.fieldName} {menuName}</Dialog.Title>
                    <button onClick={() => setIsEditOpen(false)}><XMarkIcon className="w-6 h-6 text-slate-400" /></button>
                  </div>
                  <div className="space-y-6">
                    <div className="space-y-1">
                   
                     <TextInput
                      label="Field Name"
                      name="fieldName"
                      placeholder="Enter the fieldName"
                      value={id}
                      onChange={handleInputChange("fieldName")}
                        readOnly={true}
                    />

                     <TextInput
                      label="Field Type"
                      name="fieldType"
                      placeholder="Enter the fieldType"
                      value={values.fieldValue}
                      onChange={handleInputChange("fieldValue")}
                    />

                    <TextInput
                      label="Score Type"
                      name="score"
                      placeholder="Enter the scoreType"
                      value={values.score.toString()}
                      onChange={handleInputChange("score")}
                    />
                    
                    </div>
                    <button onClick={() => { handleEditSubmit(); onEditSubmit(selectedRecruiter!); setIsEditOpen(false); }} className="w-full py-4 bg-indigo-600 text-white font-black rounded-2xl shadow-xl">Update {menuName}</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>







      {/* --- DELETE MODAL --- */}
      <Transition show={isDeleteOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={() => setIsDeleteOpen(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-md bg-white rounded-[2.5rem] p-8 text-center shadow-2xl">
                  <div className="w-20 h-20 bg-rose-50 rounded-3xl flex items-center justify-center mx-auto mb-6 text-rose-500">
                    <ExclamationTriangleIcon className="w-10 h-10" />
                  </div>
                  <Dialog.Title className="text-2xl font-black text-slate-800 mb-2">Confirm Removal</Dialog.Title>
                  <p className="text-sm text-slate-500 font-medium mb-8 px-4">
                    Are you sure you want to delete <span className="text-slate-800 font-bold">{selectedRecruiter?.fieldName}</span>?
                  </p>
                  <div className="flex gap-3">
                    <button onClick={() => setIsDeleteOpen(false)} className="flex-1 py-4 bg-slate-100 text-slate-600 font-bold rounded-2xl">Cancel</button>
                    <button onClick={() => { handleDeleteSubmit(); if(selectedRecruiter) onDeleteConfirm(selectedRecruiter.fieldName); setIsDeleteOpen(false); }} className="flex-1 py-4 bg-rose-500 text-white font-bold rounded-2xl">Delete</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

const InputField = ({ label, placeholder, onChange }: any) => (
  <div className="space-y-1">
    <label className="text-[10px] font-black uppercase text-slate-400 ml-1">{label}</label>
    <input 
      type="text" 
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl text-sm font-bold focus:ring-2 focus:ring-indigo-500 transition-all placeholder:text-slate-300 placeholder:font-medium" 
    />
  </div>
);

export default ScoreMappingConfigComponent;